// Groups (spec/dashboard.md section 9.3): Name · Strings over the groups endpoint; Rename is a command (a commit of its
// own after a confirmation naming the string count); + Group opens the strings tab with a new row in the typed group.
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { LocalizationGroup } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { ordinal, validName } from '../../content/localization/strings';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { AlertCircle, Folder, Pencil, Plus } from '../../ui/icons';
import { TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { useQuery } from '../../workspace/cache';
import { useWorkspace } from '../../workspace/react';

export function GroupsTab() {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const catalog = useContent();
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const canWrite = can('content_write') && !workspace.isProtected;
  const groupsQ = useQuery(`branch:${game.id}:${branch}:localization-groups`, () => client.data(ops.listLocalizationGroups, { path: { org, game: game.slug, branch } }), { head: workspace.head });
  const counts = new Map(((groupsQ.data as LocalizationGroup[] | undefined) ?? []).map((g) => [g.name, Number(g.strings)]));
  for (const s of catalog.stringList) if (!counts.has(s.group)) counts.set(s.group, catalog.stringList.filter((x) => x.group === s.group).length);
  const groups = Array.from(counts.entries()).sort((a, b) => ordinal(a[0], b[0]));
  const [renaming, setRenaming] = useState<string | null>(null);
  const [newGroup, setNewGroup] = useState('');
  const newGroupError = newGroup ? validName(newGroup) : null;

  const addGroup = () => {
    if (!newGroup || newGroupError) return;
    navigate(`${routes.branch(org, game.slug, branch, 'localization/strings')}?group=${encodeURIComponent(newGroup)}&create=1`);
  };

  return (
    <div className="loc-groups">
      <form
        className="page-toolbar loc-actions"
        onSubmit={(e) => {
          e.preventDefault();
          addGroup();
        }}
      >
        <span className="muted small">A group exists through its strings; it disappears with its last one.</span>
        <span className="spacer" />
        <span className="loc-new-group">
          <TextInput className="mono loc-new-group-input" aria-label="New group name" placeholder="NEW_GROUP" value={newGroup} onChange={(e) => setNewGroup(e.target.value)} aria-invalid={newGroupError ? true : undefined} disabled={!canWrite} />
          {newGroupError && (
            <span className="field-error" role="alert">
              {newGroupError}
            </span>
          )}
        </span>
        <Button type="submit" variant="primary" missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected || !newGroup || !!newGroupError} icon={<Plus aria-hidden="true" />}>
          Group
        </Button>
      </form>
      <div className="pane">
        {groups.length === 0 ? (
          groupsQ.loading ? (
            <div className="skeleton-rows" role="status" aria-label="Loading">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="skeleton-row">
                  <span className="skeleton" />
                  <span className="skeleton" />
                </div>
              ))}
            </div>
          ) : (
            <div className="pane-body">
              <EmptyState icon={<Folder />} title="No groups yet" body="A group appears with its first string: type a name above and create the string." />
            </div>
          )
        ) : (
          <table className="grid loc-table" data-testid="groups-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col" className="loc-num">
                  Strings
                </th>
                <th scope="col" className="loc-col-actions" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {groups.map(([name, count]) => (
                <tr key={name} className="grid-row">
                  <td>
                    <a
                      className="loc-group-link"
                      href={`${routes.branch(org, game.slug, branch, 'localization/strings')}?group=${encodeURIComponent(name)}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`${routes.branch(org, game.slug, branch, 'localization/strings')}?group=${encodeURIComponent(name)}`);
                      }}
                    >
                      <Folder className="icon-s" aria-hidden="true" />
                      <code>{name}</code>
                    </a>
                  </td>
                  <td className="loc-num">{count}</td>
                  <td className="loc-num loc-col-actions">
                    <Button size="s" variant="ghost" icon={<Pencil aria-hidden="true" />} onClick={() => setRenaming(name)} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected} aria-label={`Rename ${name}`}>
                      Rename
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {renaming !== null && (
        <RenameDialog
          from={renaming}
          count={counts.get(renaming) ?? 0}
          onClose={() => setRenaming(null)}
          onDone={(commit) => {
            if (commit) workspace.setHead(commit);
            catalog.refresh();
            groupsQ.refetch();
            setRenaming(null);
          }}
        />
      )}
    </div>
  );
}

function RenameDialog({ from, count, onClose, onDone }: { from: string; count: number; onClose: () => void; onDone: (commit: string | null) => void }) {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const confirm = useConfirm();
  const toasts = useToasts();
  const [to, setTo] = useState(from);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; keys: string[] } | null>(null);
  const problem = validName(to);
  const run = async () => {
    if (problem || to === from) return;
    const ok = await confirm({ title: `Rename ${from} to ${to}?`, body: `Moves ${count} string${count === 1 ? '' : 's'}; the ids stay, documents are untouched. This is a commit of its own, not a staged change.`, confirmLabel: 'Rename' });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const r = await client.call(ops.renameLocalizationGroup, { path: { org, game: game.slug, branch }, body: { from, to, message: null } });
      toasts.success(`Renamed ${from} to ${to} (${count} string${count === 1 ? '' : 's'}).`);
      onDone(r.commit ?? (r.data as { commit?: string | null } | undefined)?.commit ?? null);
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
      if (err.status === 409) setError({ message: err.message, keys: clashingKeys(err) });
      else setError({ message: err.message, keys: [] });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Rename group ${from}`}
      open
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={!!problem || to === from} onClick={() => void run()}>
            Rename…
          </Button>
        </>
      }
    >
      <form
        className="loc-dialog-form"
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
      >
        <Field label="New name" required error={to !== from ? problem : null} help={`${count} string${count === 1 ? '' : 's'} move; ids and documents stay as they are.`}>
          <TextInput value={to} onChange={(e) => setTo(e.target.value)} aria-label="New name" autoFocus />
        </Field>
        {error && (
          <div className="loc-alert" role="alert">
            <AlertCircle className="loc-alert-icon" aria-hidden="true" />
            <div>
            <p>{error.message}</p>
            {error.keys.length > 0 && (
              <>
                <p>These keys already exist in {to}:</p>
                <ul className="loc-clashes">
                  {error.keys.map((k) => (
                    <li key={k}>
                      <code>{k}</code>
                    </li>
                  ))}
                </ul>
              </>
            )}
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}

/** The keys a 409 names: the details' entity ids (or messages) when present, else the list in the message ("…: A, B."). */
function clashingKeys(err: ApiError): string[] {
  const fromDetails = (err.details ?? []).map((d) => d.entityId ?? d.message).filter((k): k is string => !!k);
  if (fromDetails.length > 0) return fromDetails;
  const m = /keys?:\s*(.+?)\.?$/.exec(err.message);
  return m ? m[1]!.split(',').map((k) => k.trim()).filter(Boolean) : [];
}
